import type { Distributor, InventoryItem, Product } from "@/types";
import { formatCOP, formatNumber } from "@/utils/format";

interface AdvisorTableProps {
  distributors: Distributor[];
  inventory: InventoryItem[];
  products: Product[];
  onEdit: (d: Distributor) => void;
}

const consignedValue = (
  advisorId: string,
  inventory: InventoryItem[],
  products: Product[]
): number =>
  inventory.reduce((acc, item) => {
    const units = item.stockByAdvisor[advisorId] ?? 0;
    const product = products.find((p) => p.id === item.productId);
    return acc + units * (product ? product.pricePublic : 0);
  }, 0);

const consignedUnits = (advisorId: string, inventory: InventoryItem[]): number =>
  inventory.reduce((acc, item) => acc + (item.stockByAdvisor[advisorId] ?? 0), 0);

// Tabla maestra de la red de asesores con tenencia en consignación.
const AdvisorTable = ({ distributors, inventory, products, onEdit }: AdvisorTableProps) => {
  if (distributors.length === 0) {
    return (
      <div className="glass-panel rounded-lg px-5 py-10 text-center">
        <span className="mx-auto w-12 h-12 rounded-lg bg-background-200/50 text-accent-200 flex items-center justify-center mb-3">
          <i className="ri-team-line text-2xl" />
        </span>
        <p className="text-sm text-foreground-700 font-semibold">Aún no hay asesores en la red</p>
        <p className="text-xs text-foreground-500 mt-1">
          Cuando alguien se registre desde la pantalla de acceso, aparecerá aquí automáticamente.
        </p>
      </div>
    );
  }

  return (
    <div className="glass-panel rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-foreground-500 border-b border-accent-500/15">
              <th className="text-left font-semibold px-4 py-3">Asesor</th>
              <th className="text-left font-semibold px-4 py-3">Cédula</th>
              <th className="text-left font-semibold px-4 py-3">Contacto</th>
              <th className="text-left font-semibold px-4 py-3">Ciudad</th>
              <th className="text-center font-semibold px-4 py-3">Margen</th>
              <th className="text-right font-semibold px-4 py-3">Cupo consignación</th>
              <th className="text-right font-semibold px-4 py-3">En consignación</th>
              <th className="text-center font-semibold px-4 py-3">Und.</th>
              <th className="text-center font-semibold px-4 py-3">Ficha</th>
            </tr>
          </thead>
          <tbody>
            {distributors.map((d) => (
              <tr
                key={d.id}
                className="border-b border-background-300/20 hover:bg-background-200/30 transition-colors"
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-primary-500/25 text-accent-200 flex items-center justify-center shrink-0 font-semibold">
                      {d.names.charAt(0).toUpperCase() || "?"}
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold text-foreground-950 truncate">
                        {d.names} {d.surnames}
                      </p>
                      <p className="text-[11px] text-foreground-500 truncate">{d.email || "—"}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-foreground-700">
                  {d.cedula || "—"}
                  {d.idFront && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-accent-300">
                      <i className="ri-verified-badge-line" /> Verificado
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">{d.phone || "—"}</td>
                <td className="px-4 py-3 text-foreground-700">{d.city || "—"}</td>
                <td className="px-4 py-3 text-center">
                  <span className="inline-block px-2.5 py-1 rounded-full text-[11px] font-semibold bg-secondary-500/20 text-secondary-100">
                    {d.margin}%
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-foreground-700">
                  {formatCOP(d.consignmentLimit)}
                </td>
                <td className="px-4 py-3 text-right font-semibold text-foreground-950">
                  {formatCOP(consignedValue(d.id, inventory, products))}
                </td>
                <td className="px-4 py-3 text-center text-foreground-700">
                  {formatNumber(consignedUnits(d.id, inventory))}
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => onEdit(d)}
                    className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-accent-300 hover:bg-background-200/60 cursor-pointer mx-auto"
                    aria-label={`Editar ficha de ${d.names}`}
                    title="Abrir ficha máster"
                  >
                    <i className="ri-id-card-line" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdvisorTable;